import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { SIGNUP_BONUS_AMOUNT } from '../../lib/types';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'How does Xed21 generate NCERT-aligned questions?',
    answer:
      'Xed21 uses an AI engine trained to produce questions mapped to the NCERT curriculum. You select the board, grade, subject, topic, question type, difficulty, and Bloom\'s Taxonomy level. The AI generates pedagogically sound questions with plausible distractors, correct answers, and detailed explanations — all aligned to your selected NCERT topic.',
  },
  {
    question: "What is Bloom's Taxonomy and why does it matter for CBSE assessments?",
    answer:
      "Bloom's Taxonomy is a framework that classifies questions by cognitive complexity — from Remembering (basic recall) to Creating (original thinking). CBSE and NEP 2020 guidelines require schools to include higher-order thinking questions in assessments. Xed21 lets you select the exact Bloom's level for every question, so your papers are balanced across all six levels by design.",
  },
  {
    question: 'What question types does Xed21 support?',
    answer:
      'Xed21 supports 6 question formats: Multiple Choice (MCQ), Multiple Select (MSQ), True/False, Fill in the Blanks, Inline Choice, and Matching. Each format generates with answer keys and explanations included.',
  },
  {
    question: 'How much does Xed21 cost?',
    answer:
      `Xed21 uses pay-as-you-go pricing starting at ₹5 per question for Remembering-level questions, up to ₹20 for Creating-level questions. You get ₹${SIGNUP_BONUS_AMOUNT} free wallet credit on signup — enough to generate a starter question paper. There is no subscription, no annual contract, and your wallet balance never expires.`,
  },
  {
    question: 'Can I use Xed21 for ICSE, State Boards, or IB?',
    answer:
      'Xed21 currently supports NCERT/CBSE curriculum. Support for ICSE, IB, IGCSE, and State Boards is being built based on educator demand. You can join the board waitlist on this page to tell us which curriculum to prioritize next and get notified when it launches.',
  },
  {
    question: 'Can schools and institutions use Xed21?',
    answer:
      'Yes. Schools and institutions can onboard their teachers with bulk wallet credit and institutional billing. Academic coordinators use Xed21 to standardize question paper quality across departments without expensive annual software contracts. Contact us for school onboarding.',
  },
  {
    question: 'What happens if a question generation fails?',
    answer:
      'If a generation fails for any reason, the wallet charge is automatically refunded. You only pay for successfully generated questions.',
  },
  {
    question: 'Can I edit the AI-generated questions?',
    answer:
      'Yes. Every generated question can be edited inline — you can modify the question text, options, correct answers, explanations, and difficulty. You can also bundle edited questions into timed assessments with custom point values.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 sm:py-28 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
            <HelpCircle className="w-3.5 h-3.5" />
            Frequently asked questions
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
            Common Questions About Xed21
          </h2>
          <p className="text-lg text-slate-500 mt-4">
            Everything educators ask before generating their first NCERT question paper.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={faq.question}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-shadow hover:shadow-sm"
            >
              <button
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={openIndex === index}
                className="w-full flex items-start gap-3 px-5 py-4 text-left"
              >
                <ChevronDown
                  className={`w-5 h-5 text-teal-600 mt-0.5 flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
                <span className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">
                  {faq.question}
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  openIndex === index ? 'max-h-96 pb-5' : 'max-h-0'
                }`}
              >
                <p className="px-5 pl-13 text-sm text-slate-600 leading-relaxed" style={{ paddingLeft: '3.25rem' }}>
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
